import type { Section } from "vanilla-cookieconsent";

import { ConsentModel } from "@/api";
import {
  CategorySettings,
  ESSENTIAL_CATEGORY_ID,
} from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/schema";
import { getLegibleTextColor } from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/ConfigContent/FreshpaintConfigContent/sections/FreshpaintThemeConfig/utils";

/** Fallback when no theme background is configured; matches the SDK default. */
const DEFAULT_CONSENT_MODAL_BACKGROUND = "#232323";

type FreshpaintColorSource = {
  colorStyles?: Record<string, string>;
  simpleBackgroundColor?: string;
};

/**
 * Picks the effective consent modal background color: explicit `--cc-bg` override
 * first, then the simplified theme picker, then the SDK default.
 */
function resolveConsentModalBackgroundColor(
  config: FreshpaintColorSource,
): string {
  return (
    config.colorStyles?.["--cc-bg"] ??
    config.simpleBackgroundColor ??
    DEFAULT_CONSENT_MODAL_BACKGROUND
  );
}

/**
 * Returns the dismiss button color the SDK should render. The value is saved
 * onto `freshpaintConfig.consentModalDismissButtonColor` so the SDK and CDN
 * stylesheet builder can use it without re-deriving.
 */
export function resolveConsentModalDismissButtonColor(
  config: FreshpaintColorSource,
): string {
  return getLegibleTextColor(resolveConsentModalBackgroundColor(config));
}

export type FreshpaintCategoriesMap = Record<
  string,
  {
    enabled: boolean;
    readOnly?: boolean;
  }
>;

function compareCategoryIds(a: string, b: string): number {
  if (a === b) return 0;
  if (a === ESSENTIAL_CATEGORY_ID) return -1;
  if (b === ESSENTIAL_CATEGORY_ID) return 1;
  return a.localeCompare(b);
}

function sortFreshpaintCategories(
  categories: FreshpaintCategoriesMap,
): FreshpaintCategoriesMap {
  return Object.fromEntries(
    Object.entries(categories).sort(([a], [b]) => compareCategoryIds(a, b)),
  );
}

/**
 * Preview-only counterpart to perfalytics-js's `deriveCategoriesFromSettings`.
 *
 * The in-form preview iframe runs vanilla-cookieconsent directly (not the
 * Freshpaint SDK), so it needs the `categories` map in the same shape the
 * SDK would produce at runtime. Form save no longer calls this — only
 * `categorySettings` is persisted; the SDK derives `categories` itself at
 * config build time. Keep this in sync with the SDK's derivation:
 * `perfalytics-js/src/consent-management/freshpaint/config-builder.ts ::
 * deriveCategoriesFromSettings`.
 *
 * - `essential` is always emitted enabled + readOnly.
 * - `alwaysOn` non-essential categories are emitted enabled + readOnly. The
 *   form UI does not expose this for non-essential, but the runtime still
 *   honors it if it appears in saved data.
 * - `hidden` categories are OMITTED rather than emitted with
 *   `enabled: false`. If we emitted them, the SDK's `acceptCategory('all')`
 *   (fired by the modal's "Accept all" button) would consent to them anyway,
 *   defeating the user's intent. `readOnly: true` isn't a workable
 *   alternative because of vanilla-cookieconsent v3.1.0 #805 (readOnly
 *   coerces enabled to true). Omitting keeps them out of
 *   `acceptedCategories` so destinations resolve to `false`, and
 *   `filterPreferencesSectionsByDisplay` already hides the modal section so
 *   visitors can't toggle them.
 * - `optional` categories are emitted with `enabled` set from the consent
 *   model (opt-out -> true, opt-in -> false).
 *
 * - Categories from the mapping, settings, and preferences sections are
 *   included unless explicitly hidden. Missing settings default to optional.
 */
export function buildFreshpaintCategories(
  categoryMapping: Record<string, string> | undefined,
  consentModel: ConsentModel,
  categorySettings?: Record<string, CategorySettings>,
  language?: PrefsLikeLanguage,
): FreshpaintCategoriesMap {
  const categories: FreshpaintCategoriesMap = {};

  categories[ESSENTIAL_CATEGORY_ID] = { enabled: true, readOnly: true };

  const categoryIds = new Set([
    ...Object.keys(categoryMapping ?? {}),
    ...Object.keys(categorySettings ?? {}),
  ]);
  for (const translation of Object.values(language?.translations ?? {})) {
    const sections = translation?.preferencesModal?.sections;
    if (!Array.isArray(sections)) continue;
    for (const section of sections) {
      if (section.linkedCategory) categoryIds.add(section.linkedCategory);
    }
  }

  for (const categoryId of categoryIds) {
    if (categoryId === ESSENTIAL_CATEGORY_ID) continue;

    const behavior = categorySettings?.[categoryId]?.behavior ?? "optional";

    if (behavior === "hidden") continue;
    if (behavior === "alwaysOn") {
      categories[categoryId] = { enabled: true, readOnly: true };
      continue;
    }
    categories[categoryId] = { enabled: consentModel === "opt-out" };
  }

  return sortFreshpaintCategories(categories);
}

/**
 * Drop preferences-modal sections whose `linkedCategory` has `behavior:
 * "hidden"`. The destination -> category mapping stays intact so consent
 * enforcement still gates destinations correctly; the category is just
 * invisible in the modal.
 *
 * `essential` is exempt — it is always shown regardless of saved settings.
 */
export function filterPreferencesSectionsByDisplay(
  sections: Section[],
  categorySettings?: Record<string, CategorySettings>,
): Section[] {
  if (!categorySettings) return sections;
  return sections.filter((section) => {
    if (!section.linkedCategory) return true;
    if (section.linkedCategory === ESSENTIAL_CATEGORY_ID) return true;
    return categorySettings[section.linkedCategory]?.behavior !== "hidden";
  });
}

/**
 * Reorder preferences-modal sections so category-linked sections appear with
 * Essential first and the rest alphabetical by `linkedCategory`. Sections
 * without a `linkedCategory` (info-only entries such as the intro description)
 * keep their original index; only the linked sections shuffle among the
 * remaining slots. Pure — does not mutate input.
 */
export function sortPreferencesModalSections<
  S extends { linkedCategory?: string },
>(sections: S[]): S[] {
  const result: S[] = new Array(sections.length);
  const linked: S[] = [];
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    if (section.linkedCategory) {
      linked.push(section);
    } else {
      result[i] = section;
    }
  }
  linked.sort((a, b) => {
    const aIsEssential = a.linkedCategory === ESSENTIAL_CATEGORY_ID;
    const bIsEssential = b.linkedCategory === ESSENTIAL_CATEGORY_ID;
    if (aIsEssential !== bIsEssential) return aIsEssential ? -1 : 1;
    return (a.linkedCategory ?? "").localeCompare(b.linkedCategory ?? "");
  });
  let cursor = 0;
  for (let i = 0; i < result.length; i++) {
    if (result[i] === undefined) result[i] = linked[cursor++];
  }
  return result;
}

export interface PrefsLikeLanguage {
  translations: Record<
    string,
    {
      preferencesModal: {
        sections: Section[];
      } & Record<string, unknown>;
    } & Record<string, unknown>
  >;
  [k: string]: unknown;
}

/**
 * Returns a copy of the language block where every translation's
 * preferencesModal.sections list has hidden categories removed. Pure — does
 * not mutate the input.
 */
export function withFilteredPrefsSections<L extends PrefsLikeLanguage>(
  language: L,
  categorySettings?: Record<string, CategorySettings>,
): L {
  if (!categorySettings) return language;

  const nextTranslations: PrefsLikeLanguage["translations"] = {};
  for (const [lang, t] of Object.entries(language.translations)) {
    // Translations sourced from older configs may be missing
    // `preferencesModal` entirely or have non-array `sections`. Pass those
    // through unchanged rather than crashing autosave on save/preview.
    const sections = t?.preferencesModal?.sections;
    if (!Array.isArray(sections)) {
      nextTranslations[lang] = t;
      continue;
    }
    nextTranslations[lang] = {
      ...t,
      preferencesModal: {
        ...t.preferencesModal,
        sections: filterPreferencesSectionsByDisplay(
          sections,
          categorySettings,
        ),
      },
    };
  }

  return {
    ...language,
    translations: nextTranslations,
  };
}

function withFilteredPrefsSectionsByCategories<L extends PrefsLikeLanguage>(
  language: L,
  categories: FreshpaintCategoriesMap,
): L {
  const allowed = new Set([...Object.keys(categories), ESSENTIAL_CATEGORY_ID]);
  const nextTranslations: PrefsLikeLanguage["translations"] = {};
  for (const [lang, translation] of Object.entries(language.translations)) {
    const sections = translation?.preferencesModal?.sections;
    if (!Array.isArray(sections)) {
      nextTranslations[lang] = translation;
      continue;
    }
    nextTranslations[lang] = {
      ...translation,
      preferencesModal: {
        ...translation.preferencesModal,
        sections: sections.filter(
          (section) =>
            !section.linkedCategory || allowed.has(section.linkedCategory),
        ),
      },
    };
  }
  return { ...language, translations: nextTranslations };
}

/**
 * Only the first info-only section is treated as the editable intro
 * description. Any other info-only sections (e.g. legacy footers
 * interleaved between category toggles) are preserved in `sections` so
 * downstream consumers / the SDK can still render them.
 */
export function liftDescriptionFromIntroSection<L extends PrefsLikeLanguage>(
  language: L,
): L {
  const nextTranslations: PrefsLikeLanguage["translations"] = {};
  for (const [lang, t] of Object.entries(language.translations)) {
    const prefs = t?.preferencesModal;
    if (!prefs || typeof prefs !== "object") {
      nextTranslations[lang] = t;
      continue;
    }
    const sections = Array.isArray(prefs.sections) ? prefs.sections : [];
    const first = sections[0];
    if (first && !first.linkedCategory) {
      const introDescription =
        typeof first.description === "string" ? first.description : "";
      nextTranslations[lang] = {
        ...t,
        preferencesModal: {
          ...prefs,
          description: introDescription,
          sections: sections.slice(1),
        },
      };
      continue;
    }
    const existingDescriptionRaw = (prefs as { description?: unknown })
      .description;
    const existingDescription =
      typeof existingDescriptionRaw === "string" ? existingDescriptionRaw : "";
    nextTranslations[lang] = {
      ...t,
      preferencesModal: {
        ...prefs,
        description: existingDescription,
        sections,
      },
    };
  }
  return { ...language, translations: nextTranslations };
}

export function injectIntroSectionFromDescription<L extends PrefsLikeLanguage>(
  language: L,
): L {
  const nextTranslations: PrefsLikeLanguage["translations"] = {};
  for (const [lang, t] of Object.entries(language.translations)) {
    const prefs = t?.preferencesModal;
    if (!prefs || typeof prefs !== "object") {
      nextTranslations[lang] = t;
      continue;
    }
    const rawDescription = (prefs as { description?: unknown }).description;
    const description =
      typeof rawDescription === "string" ? rawDescription : "";
    const sections = Array.isArray(prefs.sections) ? prefs.sections : [];
    const prefsRecord = prefs as Record<string, unknown>;
    const prefsWithoutDescription = Object.fromEntries(
      Object.entries(prefsRecord).filter(([k]) => k !== "description"),
    );
    const nextSections =
      description.trim() === ""
        ? sortPreferencesModalSections(sections)
        : sortPreferencesModalSections([{ description }, ...sections]);
    nextTranslations[lang] = {
      ...t,
      preferencesModal: { ...prefsWithoutDescription, sections: nextSections },
    };
  }
  return { ...language, translations: nextTranslations };
}

/**
 * Lowers a slot's persisted `categorySettings` into the runtime shape the
 * in-form preview iframe needs: a derived `categories` map (so vanilla-
 * cookieconsent can render toggles) plus a `language` whose preferences-modal
 * sections have hidden categories filtered out (so the preview matches what
 * the SDK's section filter will produce in production).
 *
 * Preview-only. Form save persists `categorySettings` directly and the SDK
 * derives `categories` at runtime — see `buildFreshpaintCategories` above.
 *
 * Conditional configs are themselves slots; apply this once per conditional
 * with that conditional's own consent model.
 */
export function applyTransportDerivations<
  T extends {
    categorySettings?: Record<string, CategorySettings>;
    categories?: FreshpaintCategoriesMap;
    language?: PrefsLikeLanguage;
  },
>(
  slot: T,
  categoryMapping: Record<string, string> | undefined,
  consentModel: ConsentModel,
): T & { categories: FreshpaintCategoriesMap } {
  const categories = sortFreshpaintCategories(
    slot.categorySettings === undefined && slot.categories
      ? Object.fromEntries(
          Object.entries(slot.categories).map(([key, value]) => [
            key,
            consentModel === "opt-in" && !value.readOnly
              ? { ...value, enabled: false }
              : value,
          ]),
        )
      : buildFreshpaintCategories(
          categoryMapping,
          consentModel,
          slot.categorySettings,
          slot.language,
        ),
  );
  const language = slot.language
    ? injectIntroSectionFromDescription(
        withFilteredPrefsSectionsByCategories(slot.language, categories),
      )
    : slot.language;
  return { ...slot, categories, language };
}
